import { Typography } from "@material-ui/core";
import { DefaultButtonWithTooltip } from "Components/Shared/Buttons/DefaultButtonWithTooltip";
import { SettingsIcon } from "Components/Shared/Icons/SettingsIcon";
import { RightPanel } from "Components/Layout/RightPanel";
import * as React from "react";
import styled from "styled-components";
import { Resources, useResource } from "Translations/Resources";
import { UnstyledLink } from "Components/Shared/Routes/UnstyledLink";
import { AccessRightCodes } from "Components/Models/AccessRights";
import { Authorized } from "Routes/Authorized";
import { useDispatch } from "react-redux";
import { resetUser } from "State/Auth/AuthReducer";
import { Guidepost } from "Components/Guidepost/Guidepost";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { BreadcrumbItem, Breadcrumbs } from "Components/Layout/Breadcrumbs";

const StyledRoot = styled.div`
  display: flex;
  min-height: ${props => props.theme.vh(100)};

  ${props => props.theme.breakpoints.down("xs")} {
    flex-flow: column;
  }
`;

const StyledMain = styled.main`
  flex-grow: 1;
  display: flex;

  .main-wrapper {
    flex-grow: 1;
  }
`;

const StyledContent = styled.div<{
  centerContent?: boolean;
  isDashboard?: boolean;
}>`
  flex: 1;
  background-color: ${props => props.theme.colors.body};
  padding: ${props => props.theme.spacing(2, 4, 2.5)};
  display: ${props => (props.centerContent ? "grid" : "")};
  justify-content: ${props => (props.centerContent ? "center" : "")};
  grid-template-rows: auto 1fr;

  ${props => props.theme.breakpoints.down("xs")} {
    padding: ${props =>
      props.isDashboard
        ? props.theme.spacing(0, 1.5)
        : props.theme.spacing(1, 1.5)};
  }
`;

const StyledChildren = styled.div<{
  hasSideMenu?: boolean;
}>`
  margin-top: 24px;

  ${props =>
    props.hasSideMenu &&
    `display: flex;

    .box {
      margin-top: 0;
    }`}
`;

const StyledNav = styled.nav`
  z-index: 100;

  ${props => props.theme.breakpoints.down("xs")} {
    .ibis-guidepost-wrapper {
      width: 100%;
      height: 50px;
    }
    .ibis-guidepost-panel {
      right: 0;
      width: inherit;
      height: 50px;
      grid-template-rows: auto;
      grid-template-columns: 1fr auto;
      padding: ${props => props.theme.spacing(0, 1)};
      align-items: center;

      .ibis-guidepost-list {
        display: flex;
        padding: 0;
      }
      .ibis-guidepost-toggle {
        margin: 0;
      }
      .ibis-guidepost-app {
        margin: ${props => props.theme.spacing(0, 1)};
      }
      hr ~ .ibis-guidepost-app {
        display: none;
      }
    }
  }
`;

const StyledTopBar = styled.div<{
  isDashboard?: boolean;
}>`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${props => props.theme.spacing(0, 2)};

  ${props => props.theme.breakpoints.down("xs")} {
    ${props => props.isDashboard && `display: none;`}
  }

  h1 {
    min-height: 30px;
  }
`;

const IconsWrapper = styled.div`
  display: flex;
  align-items: center;

  .btn-create {
    padding: 6px 15px;
    width: auto;
    min-width: 130px;
    margin-left: 10px;
  }
`;

const StyledRightPanel = styled.div<{ hideDown: number }>`
  @media (max-width: ${props => props.hideDown}px) {
    display: none;
  }
`;

type Props = {
  title: string;
  cssClass?: string;
  rightPanel?: {
    open: boolean;
    setOpen: (val: boolean) => void;
    width: number;
    getBody: () => JSX.Element;
    hideDown?: number;
  };
  create?: {
    accessRightCodes: AccessRightCodes[];
    url: string;
    title: string;
  };
  settingsUrl?: string;
  getSearchBody?: () => JSX.Element;
  centerContent?: boolean;
  hasSideMenu?: boolean;
  breadcrumbItems?: BreadcrumbItem[];
  isDashboard?: boolean;
};

export const AuthenticatedLayout: React.FunctionComponent<Props> = props => {
  const { t } = useResource();
  const dispatch = useDispatch();

  return (
    <StyledRoot>
      <StyledNav>
        <Guidepost handleLogout={() => dispatch(resetUser())} />
      </StyledNav>

      <StyledMain>
        <div className={"main-wrapper"}>
          <StyledContent
            centerContent={props.centerContent}
            isDashboard={props.isDashboard}
          >
            <StyledTopBar isDashboard={props.isDashboard}>
              <div>
                <Typography variant={"h1"}>{props.title}</Typography>
                {props.breadcrumbItems && (
                  <Breadcrumbs breadcrumbItems={props.breadcrumbItems} />
                )}
              </div>
              <div>
                <IconsWrapper>
                  {props.getSearchBody && props.getSearchBody()}
                  {props.settingsUrl && (
                    <UnstyledLink to={props.settingsUrl}>
                      <DefaultButtonWithTooltip
                        title={t(Resources.Common.Settings)}
                      >
                        <SettingsIcon />
                      </DefaultButtonWithTooltip>
                    </UnstyledLink>
                  )}
                  {props.create && (
                    <Authorized accessRights={props.create.accessRightCodes}>
                      <UnstyledLink to={props.create.url}>
                        <PrimaryButton type={"button"} className={"btn-create"}>
                          {props.create.title}
                        </PrimaryButton>
                      </UnstyledLink>
                    </Authorized>
                  )}
                </IconsWrapper>
              </div>
            </StyledTopBar>

            <StyledChildren
              className={props.cssClass}
              hasSideMenu={props.hasSideMenu}
            >
              {props.children}
            </StyledChildren>
          </StyledContent>
        </div>

        {props.rightPanel && (
          <StyledRightPanel hideDown={props.rightPanel.hideDown ?? 0}>
            <RightPanel
              getBody={props.rightPanel.getBody}
              open={props.rightPanel.open}
              setOpen={props.rightPanel.setOpen}
              width={props.rightPanel.width}
              allowDrawer={!props.rightPanel.hideDown}
            />
          </StyledRightPanel>
        )}
      </StyledMain>
    </StyledRoot>
  );
};
